import { AsyncPipe, NgIf } from '@angular/common';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { BoondockFacade } from '@curbnturf/boondock-listing';
import { Boondock } from '@curbnturf/entities';
import { RatingFacade } from '@curbnturf/rating';
import * as RatingActions from '@curbnturf/rating/src/lib/+state/rating.actions';
import { ReviewPhotosComponent } from '@curbnturf/site-components/src/lib/review-photos/review-photos.component';
import { IonicModule, LoadingController, NavController } from '@ionic/angular';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, Subscription } from 'rxjs';

@Component({
  selector: 'curbnturf-rating-boondock-description',
  templateUrl: './rating-boondock-description.component.html',
  styleUrls: ['./rating-boondock-description.component.scss'],
  standalone: true,
  imports: [AsyncPipe, FormsModule, IonicModule, NgIf, ReactiveFormsModule, ReviewPhotosComponent, RouterModule],
})
export class RatingBoondockDescriptionComponent implements OnInit {
  @Output() navigateBack: EventEmitter<void> = new EventEmitter<void>();

  @Output() continue: EventEmitter<void> = new EventEmitter<void>();

  @Output() skip: EventEmitter<void> = new EventEmitter<void>();

  subscriptions: Subscription[] = [];

  boondock$: Observable<Boondock | null>;
  boondockId: number | undefined;

  appContext: boolean;

  descriptionForm: FormGroup<{
    id: FormControl<number | null>;
    description: FormControl<string | null>;
    resourceUrl: FormControl<string | null>;
    photos: FormArray<FormControl<string | null>>;
  }>;

  constructor(
    private updates$: Actions,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private loadingController: LoadingController,
    private navCtrl: NavController,
    private ratingFacade: RatingFacade,
    private boondockFacade: BoondockFacade,
  ) {}

  ngOnInit(): void {
    this.descriptionForm = this.fb.group<{
      id: FormControl<number | null>;
      description: FormControl<string | null>;
      resourceUrl: FormControl<string | null>;
      photos: FormArray<FormControl<string | null>>;
    }>({
      id: this.fb.control<number | null>(null),
      description: this.fb.control<string | null>(''),
      resourceUrl: this.fb.control<string | null>(''),
      photos: this.fb.nonNullable.array<string | null>([], { updateOn: 'blur' }), // array of URLs
    });

    this.boondock$ = this.boondockFacade.selected$;
    this.subscriptions.push(
      this.boondock$.subscribe((boondock) => {
        if (boondock) {
          this.boondockId = boondock.id;
          this.descriptionForm.patchValue({ id: boondock.id });
        }
      }),
      this.route.data.subscribe((data: { appContext: boolean }) => {
        this.appContext = data.appContext || false;
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => {
      if (!sub.closed) {
        sub.unsubscribe();
      }
    });
  }

  async onContinue() {
    const boondock = new Boondock({
      id: this.boondockId,
      description: this.descriptionForm.value.description || '',
      url: this.descriptionForm.value.resourceUrl || '',
      photos: [],
    });

    const loading = await this.loadingController.create({
      message: 'Saving...',
      duration: 2000,
    });
    await loading.present();

    // Make the subscription before sending the request to make sure we don't miss anything.
    this.subscriptions.push(
      this.updates$.pipe(ofType(RatingActions.selectCaretakerBoondock)).subscribe(async () => {
        await loading.dismiss();
        // Emit event for web and navigate for app.
        if (this.appContext) {
          await this.navCtrl.navigateForward(['rating', 'boondock', this.boondockId, 'complete']);
        } else {
          this.continue.emit();
        }
      }),
    );

    this.ratingFacade.selectCaretakerBoondock({
      boondockId: this.boondockId,
      boondock,
    });
  }

  async onSkip() {
    // Emit event for web and navigate for app.
    if (this.appContext) {
      await this.navCtrl.navigateForward(['rating', 'boondock', this.boondockId, 'complete']);
    } else {
      this.skip.emit();
    }
  }
}
